import { AxiosInstance } from 'axios';
import { toast } from 'sonner';

import {
  CAPABILITIES,
  CAPABILITY_CONFIG_KEYS,
} from '@/sections/asset/constants';
import { JobConfig, ScanOptions } from '@/sections/asset/types';
import { Attribute } from '@/types';

/**
 * Response from the /my endpoint for Attribute queries
 */
interface AttributeDataResponse {
  attributes?: Attribute[];
}

/**
 * Creates a basic job configuration
 *
 * @param assetKey - Asset key to run the job against
 * @param capabilities - Optional list of capabilities
 * @param config - Optional configuration
 * @returns Job configuration object
 */
export function createJobConfig(
  assetKey: string,
  capabilities?: string[],
  config?: Record<string, string>
): JobConfig {
  return {
    assetKey,
    ...(capabilities && { capabilities }),
    ...(config && { config }),
  };
}

/**
 * Builds jobs for simple capability scans (port, network)
 *
 * @param assets - Assets to scan
 * @param capability - Capability to execute
 * @returns Array of job configurations
 */
export function buildSimpleCapabilityJobs(
  assets: { key: string }[],
  capability: string
): JobConfig[] {
  return assets.map(asset => createJobConfig(asset.key, [capability]));
}

/**
 * Builds jobs for comprehensive scans
 * Runs multiple capabilities based on asset attributes
 *
 * @param assets - Assets to scan
 * @param axios - Axios instance for API calls
 * @param options - Scan options (random user agent)
 * @returns Array of job configurations
 */
export async function buildComprehensiveScanJobs(
  assets: { key: string }[],
  axios: AxiosInstance,
  options?: ScanOptions
): Promise<JobConfig[]> {
  const jobs: JobConfig[] = [];
  const randomUserAgent = options?.randomUserAgent ? 'true' : 'false';

  // Parallelize fetching attributes for all assets
  const results = await Promise.allSettled(
    assets.map(async asset => {
      try {
        // Add base job for the asset (runs default capabilities)
        jobs.push(createJobConfig(asset.key));

        // Fetch attributes to find HTTP services
        const { data } = await axios.get<AttributeDataResponse>('/my', {
          params: {
            key: `source:${asset.key}`,
            exact: true,
          },
        });

        const httpAttributes = (data?.attributes || []).filter(attr =>
          attr.name.startsWith('http')
        );

        // Add crawler jobs for HTTP attributes
        return httpAttributes.map(attr =>
          createJobConfig(attr.key, [CAPABILITIES.CRAWLER], {
            [CAPABILITY_CONFIG_KEYS.RANDOM_USER_AGENT]: randomUserAgent,
          })
        );
      } catch (error) {
        console.error(
          `Failed to fetch data for comprehensive scan: ${asset.key}`,
          error
        );
        toast.error(`Failed to fetch data for asset ${asset.key}`);
        return [];
      }
    })
  );

  // Flatten successful results and add to jobs
  const additionalJobs = results
    .filter(
      (r): r is PromiseFulfilledResult<JobConfig[]> => r.status === 'fulfilled'
    )
    .flatMap(r => r.value);

  jobs.push(...additionalJobs);

  return jobs;
}
